import { useEffect } from 'react';
import { Button, DatePicker, Form, Modal, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import type { UpdateAssetLogRequest } from '@evimo/types/api/goldenRecord/update-asset-log/update-asset-log.request.dto';
import { JsonView } from '~/app/components/common/JsonView/JsonView';
import { formatJsonValue } from '~/app/utils/json';
import { AssetLogJsonPayload } from './AssetLogJsonPayload';
import { copyAssetLogPayload } from './assetLogContent.utils';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';

const { Text } = Typography;

type FormValues = {
  receivedAt: Dayjs;
  rawLog: unknown;
};

type Props = {
  log: AssetLogListItem | null;
  open: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: UpdateAssetLogRequest) => Promise<void>;
};

function toRawLogPayload(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  return formatJsonValue(value);
}

export function AssetLogModal({ log, open, isSaving, onClose, onSave }: Props) {
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation('assets');
  const isEditable = !!log && !log.isTreated;

  useEffect(() => {
    if (!open || !log) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      receivedAt: dayjs(log.receivedAt ?? log.createdAt),
      rawLog: log.rawLog,
    });
  }, [form, log, open]);

  const handleSave = async () => {
    const values = await form.validateFields();

    await onSave({
      receivedAt: values.receivedAt.toISOString(),
      rawLog: toRawLogPayload(values.rawLog),
    });
  };

  const handleCopy = async () => {
    const ok = await copyAssetLogPayload(log?.rawLog);
    if (ok) {
      message.success(t('detail.logs.copySuccess', 'Payload copied'));
      return;
    }
    message.error(t('detail.logs.copyError', 'Failed to copy payload'));
  };

  return (
    <Modal
      open={open}
      title={t('rawLogModal.title', { logId: log?.logId })}
      onCancel={onClose}
      width={720}
      destroyOnHidden
      centered
      styles={{
        header: {
          paddingInline: 32,
          paddingTop: 16,
          paddingBottom: 0,
        },
        body: {
          height: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingInline: 32,
          paddingBlock: 16,
        },
        footer: {
          paddingInline: 32,
          paddingTop: 12,
          paddingBottom: 24,
        },
      }}
      footer={
        isEditable
          ? [
              <Button key="cancel" onClick={onClose} disabled={isSaving}>
                {t('detail.actions.reset')}
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={() => void handleSave()}
                loading={isSaving}
              >
                {t('rawLogModal.save')}
              </Button>,
            ]
          : null
      }
    >
      <Text type="secondary">{t('rawLogModal.description')}</Text>

      <Form<FormValues> form={form} layout="vertical" component={false}>
        {isEditable ? (
          <div style={{ marginTop: 16 }}>
            <Form.Item<FormValues>
              name="receivedAt"
              label={t('rawLogModal.receivedAt')}
              rules={[{ required: true }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item<FormValues>
              name="rawLog"
              label={t('rawLogModal.content')}
              rules={[{ required: true }]}
            >
              <JsonView
                editable
                rows={25}
                style={{ maxHeight: '100%' }}
                placeholder={t('rawLogModal.contentPlaceholder')}
              />
            </Form.Item>

            <Text type="secondary">{t('rawLogModal.untreatedHint')}</Text>
          </div>
        ) : null}
      </Form>

      {!isEditable ? (
        <section className={styles.payloadCard} style={{ marginTop: 16 }}>
          <div className={styles.payloadHeader}>
            <h4 className={styles.payloadTitle}>
              {t('detail.logs.payloadTitle', 'Payload (JSON)')}
            </h4>
            <Button
              type="default"
              size="small"
              icon={<CopyOutlined />}
              className={styles.payloadCopyButton}
              onClick={() => {
                void handleCopy();
              }}
            >
              {t('detail.logs.copy', 'Copy')}
            </Button>
          </div>
          <div className={styles.payloadBody}>
            <AssetLogJsonPayload value={log?.rawLog} maxHeight={480} />
          </div>
        </section>
      ) : null}
    </Modal>
  );
}
